"use client";

import { BellIcon } from "@heroicons/react/20/solid";
import {
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import clsx from "clsx";
import {
	Dropdown,
	DropdownButton,
	DropdownDivider,
	DropdownHeader,
	DropdownItem,
	DropdownMenu,
} from "@/components/catalyst/Dropdown";
import { orpc } from "@/orpc/client";
import { formatShortDate } from "@/util/date";

export function NotificationDropdown() {
	const queryClient = useQueryClient();

	const { data: unreadCountData } = useQuery(
		orpc.notifications.count.queryOptions(),
	);

	const {
		data: notificationsData,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useInfiniteQuery(
		orpc.notifications.list.infiniteOptions({
			input: (pageParam) => ({
				limit: pageParam ? 10 : 20,
				cursor: pageParam as string | undefined,
			}),
			initialPageParam: undefined as string | undefined,
			getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
		}),
	);

	const notifications =
		notificationsData?.pages.flatMap((p) => p.notifications) ?? [];

	const onMarkReadSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: orpc.notifications.count.queryKey(),
		});
		queryClient.invalidateQueries({
			queryKey: orpc.notifications.list.key(),
		});
	};

	const markRead = useMutation(
		orpc.notifications.markRead.mutationOptions({
			onSuccess: onMarkReadSuccess,
		}),
	);

	const markAllRead = useMutation(
		orpc.notifications.markAllRead.mutationOptions({
			onSuccess: onMarkReadSuccess,
		}),
	);

	return (
		<Dropdown>
			<DropdownButton
				plain
				className="relative -m-2.5 p-2.5 text-neutral-content! hover:text-neutral-content/80 cursor-pointer active:text-neutral-content/60 active:outline-2 active:outline-primary rounded-full! data-active:text-neutral-content/80 data-hover:text-neutral-content/80 focus:outline-none"
			>
				<span className="sr-only">View notifications</span>
				<BellIcon aria-hidden="true" className="size-6" />
				{(unreadCountData?.count ?? 0) > 0 && (
					<span className="absolute top-2 right-2 size-2 rounded-full bg-error ring-2 ring-white dark:ring-neutral" />
				)}
			</DropdownButton>
			<DropdownMenu
				anchor="bottom end"
				className="w-96! max-h-[70vh]! sm:max-h-[85vh]!"
			>
				<DropdownHeader className="flex items-center justify-between">
					<span className="text-base-content font-medium text-sm">
						Notifications
					</span>
					{(unreadCountData?.count ?? 0) > 0 && (
						<button
							type="button"
							className="text-xs font-normal text-accent hover:text-accent/80 cursor-pointer"
							onClick={(e) => {
								e.preventDefault();
								markAllRead.mutate(undefined);
							}}
						>
							Mark all as read
						</button>
					)}
				</DropdownHeader>
				<DropdownDivider />
				{notifications.length === 0 ? (
					<div className="px-4 py-3 text-sm text-base-content/50 text-center">
						No notifications
					</div>
				) : (
					<>
						{notifications.map((notification) => (
							<DropdownItem
								key={notification.id}
								className={clsx(
									"data-focus:bg-base-content/5 data-focus:text-base-content cursor-pointer",
									!notification.read && "bg-primary/5",
								)}
								onClick={() => {
									if (!notification.read) {
										markRead.mutate({ id: notification.id });
									}
								}}
							>
								<div className="flex gap-2 w-full whitespace-normal wrap-break-word">
									{!notification.read && (
										<span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
									)}
									<div className="flex flex-col gap-1 flex-1">
										<span
											className={clsx(
												"text-base-content",
												notification.read ? "font-normal" : "font-medium",
											)}
										>
											{notification.title}
										</span>
										<span className="text-xs text-base-content/70 line-clamp-2">
											{notification.message}
										</span>
										<span className="text-[10px] text-base-content/50">
											{formatShortDate(notification.createdAt)}
										</span>
									</div>
								</div>
							</DropdownItem>
						))}
						{hasNextPage && (
							<>
								<DropdownDivider />
								<div className="col-span-full flex justify-center py-3">
									<button
										type="button"
										className="w-full text-xs font-medium text-accent/80 hover:text-accent cursor-pointer disabled:opacity-50"
										disabled={isFetchingNextPage}
										onClick={(e) => {
											e.preventDefault();
											fetchNextPage();
										}}
									>
										{isFetchingNextPage ? "Loading..." : "Load more"}
									</button>
								</div>
							</>
						)}
					</>
				)}
			</DropdownMenu>
		</Dropdown>
	);
}
